/**
 * Where an annotation sits on the X axis, and why that is two different numbers.
 *
 * A drawing is **stored** against a bar's timestamp and **resolved** through a
 * bar's index, and neither can be the other.
 *
 * The chart's X axis collapses non-trading gaps, so an index means "the 42nd bar
 * loaded" and nothing else. Store that and the
 * drawing moves the moment `barsRange` changes — load 1500 bars instead of 500
 * and every trendline slides a thousand bars left, still perfectly straight and
 * describing nothing. It also means two charts of the same symbol at different
 * bar counts disagree about where the same drawing is.
 *
 * A timestamp does not move. It is what the bar *is*, so a drawing pinned to one
 * stays on it through a refetch, a zoom, a reload, and a second chart. The cost
 * is a lookup per endpoint at render time, which is a binary search over an
 * array the feed already holds.
 *
 * So: timestamps in the contract, timestamps at the surface too — the axis is a
 * `DiscontinuousDateAxis` and places by time — and an index only in between, as
 * the answer to *which bar*. The caller converts it back to that bar's timestamp
 * before anything is drawn; see "Index in, time out" in `scichart-builder.ts`.
 * This is the only place that knows all three.
 *
 * ## A drawing can outlive the bars under it
 *
 * Draw a trendline, then load a shorter range, and one end is now off the loaded
 * history. The honest answers are to clamp it to the edge or to hide the whole
 * drawing, and clamping is the wrong one: a trendline clamped at its left end
 * has a *different slope*, which is a line nobody drew making a claim nobody
 * made. So an endpoint that falls outside is reported as such, and the caller
 * omits the drawing until the bars come back.
 */

/**
 * The bar index nearest a timestamp, or `undefined` when it is off the loaded
 * range.
 *
 * Nearest rather than at-or-after: a drawing is pinned to the bar it was drawn
 * on, and after a resample from daily to weekly that exact timestamp may not
 * exist any more. The nearest bar is the same place on the chart; the next one
 * after it can be four days later.
 */
export function indexAtTime(x: readonly number[], time: number): number | undefined {
  if (x.length === 0) return undefined;
  const first = x[0]!;
  const last = x[x.length - 1]!;
  // Outside the loaded history in either direction. Reported rather than
  // clamped — see the module docstring.
  if (time < first || time > last) return undefined;

  let lo = 0;
  let hi = x.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (x[mid]! < time) lo = mid + 1;
    else hi = mid;
  }
  // `lo` is the first bar at or after `time`; the one before it may be closer.
  const after = x[lo]!;
  const before = lo > 0 ? x[lo - 1]! : after;
  return Math.abs(after - time) <= Math.abs(time - before) ? lo : lo - 1;
}

/**
 * The bar that **contains** a timestamp — the last one at or before it.
 *
 * Different from {@link indexAtTime}, and the difference only shows on a coarser
 * frequency. A bar carries the *first* date in its bucket (`resample.py` takes
 * `date.first()`), so the weekly bar for the week of the 2nd is stamped the 2nd
 * and covers everything up to the 8th. A plan written on Friday the 6th is
 * nearer the *next* bar than its own — so "nearest" places it a week late, on a
 * bar the plan was not yet live for, and the week it actually ran shows nothing.
 *
 * "Contains" has no such edge: a timestamp belongs to the period it falls in,
 * whatever the period's length, which is the only reading that survives a
 * resample from daily to weekly to monthly.
 *
 * Nothing for a time before the first bar. That is a genuine miss rather than an
 * off-by-one, and answering bar zero would claim the ray starts at a date it has
 * nothing to do with.
 */
export function containingIndex(x: readonly number[], time: number): number | undefined {
  if (x.length === 0 || time < x[0]!) return undefined;
  let lo = 0;
  let hi = x.length - 1;
  while (lo < hi) {
    // Upper mid, so the loop settles on the last bar that is not after `time`.
    const mid = (lo + hi + 1) >> 1;
    if (x[mid]! <= time) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Where a ray starts, in index space.
 *
 * A ray says "from here rightwards", so its start is the bar the moment falls
 * in — see {@link containingIndex} — and its far end is the view's, resolved by
 * the caller because "the right edge" moves as bars arrive.
 *
 * **No clamp flag, because "containing" already is one.** The case `placeX`
 * needs `clamp` for — a plan written this afternoon against daily bars stamped
 * midnight, so its moment sits past the end of the data — is answered here by
 * construction: the last bar is still the last one at or before *now*, so it is
 * what contains it. The asymmetry survives too, since a moment before the first
 * bar has no containing bar at all.
 */
export function placeRayStart(
  x: readonly number[],
  time: number | undefined,
): number | undefined {
  return time === undefined ? undefined : containingIndex(x, time);
}

/** The timestamp of a bar index, for turning a drag back into storage. */
export function timeAtIndex(x: readonly number[], index: number): number | undefined {
  const rounded = Math.round(index);
  return rounded >= 0 && rounded < x.length ? x[rounded] : undefined;
}

/**
 * An annotation's X endpoints in index space, or `undefined` if unplaceable.
 *
 * `clamp` is for a **ray**, and only for a ray. A ray says "from here
 * rightwards" and carries one price, so pinning its start to the nearest
 * loaded bar keeps every claim it makes: the price is unchanged and the
 * direction is unchanged. Clamping a *trendline* would not be honest — a
 * clamped endpoint gives it a different slope, which is a line nobody drew.
 *
 * The case this exists for is a plan written **today**. Its `createdAt` is a
 * moment during the session; the last daily bar is that same day at midnight.
 * So the start is a few hours past the end of the data and the ray placed
 * nowhere at all — which is why a plan's levels never appeared on the chart it
 * was drawn on.
 */
export function placeX(
  x: readonly number[],
  x1: number | undefined,
  x2?: number,
  clamp = false,
): { x1: number; x2?: number } | undefined {
  if (x1 === undefined) return undefined;
  const first = indexAtTime(x, x1) ?? (clamp ? clampToRange(x, x1) : undefined);
  if (first === undefined) return undefined;
  if (x2 === undefined) return { x1: first };
  const second = indexAtTime(x, x2);
  // Both ends or neither: half a trendline is a line with a slope nobody drew.
  return second === undefined ? undefined : { x1: first, x2: second };
}

/**
 * The last bar, for a time **after** the data ends. Nothing, for one before it.
 *
 * Forward only, and the asymmetry is the whole point. A time past the last bar
 * is *now* — a plan written this afternoon against daily bars stamped midnight —
 * and the right edge is exactly where it belongs. A time before the first bar
 * genuinely predates the loaded history, and dragging it forward to bar zero
 * would draw it at a date it has nothing to do with.
 */
function clampToRange(x: readonly number[], time: number): number | undefined {
  if (x.length === 0) return undefined;
  return time > x[x.length - 1]! ? x.length - 1 : undefined;
}

/**
 * A **fractional** bar offset as a time, extrapolating past either end.
 *
 * The window is computed in bar counts — "150 bars ending at the last one, plus
 * a tenth of that as margin" — and a `DiscontinuousDateAxis` wants a time range.
 * So this is the seam between the two, and the interesting half is outside the
 * data: the right margin is *defined* as space past the last bar, so the offset
 * it asks for has no bar and must be extrapolated rather than clamped. Clamping
 * would silently delete the margin, which is the bug where turning a right
 * margin on added bars on the left instead of space on the right.
 *
 * The step is the gap between the last two bars — the only spacing this chart
 * can be said to have, since it differs per interval and per resample. Off the
 * left end it extrapolates from the first two for the same reason.
 *
 * Interpolates between bars rather than snapping, because a window edge is not a
 * bar: half a bar of margin is a real answer and rounding it to none is not.
 */
export function timeAtOffset(x: readonly number[], offset: number): number | undefined {
  if (x.length === 0) return undefined;
  if (x.length === 1) return x[0];
  const last = x.length - 1;
  if (offset >= last) {
    const step = x[last]! - x[last - 1]!;
    return x[last]! + (offset - last) * step;
  }
  if (offset <= 0) {
    const step = x[1]! - x[0]!;
    return x[0]! + offset * step;
  }
  const lower = Math.floor(offset);
  const frac = offset - lower;
  return x[lower]! + (x[lower + 1]! - x[lower]!) * frac;
}

/**
 * The inverse: a time back to a fractional bar offset.
 *
 * Needed because the axis is asked what it is showing — a pan or a wheel zoom
 * moves it, and "hold the right edge while the bar count changes" has to read
 * the current window back in the units the count is in.
 *
 * Extrapolates at both ends on the same reasoning, so a round trip through
 * {@link timeAtOffset} and back lands where it started even when the window
 * extends past the data.
 */
export function offsetAtTime(x: readonly number[], time: number): number | undefined {
  if (x.length === 0) return undefined;
  if (x.length === 1) return 0;
  const last = x.length - 1;
  if (time >= x[last]!) {
    const step = x[last]! - x[last - 1]!;
    return step === 0 ? last : last + (time - x[last]!) / step;
  }
  if (time <= x[0]!) {
    const step = x[1]! - x[0]!;
    return step === 0 ? 0 : (time - x[0]!) / step;
  }
  // The bar at or before `time`, then the fraction into the gap after it.
  let lo = 0;
  let hi = last;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (x[mid]! <= time) lo = mid;
    else hi = mid;
  }
  const gap = x[lo + 1]! - x[lo]!;
  return gap === 0 ? lo : lo + (time - x[lo]!) / gap;
}
